/**
 * Fader — a single MIDI CC on a vertical (or horizontal) slider.
 *
 * Value is 0..127. Drag along the fader's long axis sends the CC at
 * throttled ~60Hz via `fireAction`. Handle position persists across
 * reloads via the item's `value` field on the store. Centered mode
 * splits the travel into two halves — resting at 64, up sends 64..127,
 * down sends 0..64 — matching TouchOSC's `centered` flag.
 *
 * Test mode: pointerdown captures the pointer, sends the initial value,
 * and every pointermove fires a CC + updates the store. Edit mode: no
 * pointer capture, no dispatch — the wrapper takes the gesture.
 */

import { createSignal, createEffect, onCleanup, type Accessor } from 'solid-js';
import { fireAction, fanoutTargets } from '../opbridge/dispatch.ts';
import { ulid } from '../utils/ulid.ts';
import { editOwnsGesture, isEditMode, editItem, type FaderItem, type LayoutColor } from '../layout/store.ts';
import { GUARD_GRAB_HOLD_MS, GUARD_GRAB_MOVE_TOL } from '../performance/store.ts';
import type { MidiCcStep } from '@opbuttons/schema';
import { noteControlValue } from '../timeline/recorder.ts';
import { releaseTouch } from '../timeline/touch.ts';

interface FaderProps {
  item: FaderItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** Coalesce dispatches to ~60Hz so a fast drag doesn't flood the wire. */
const DISPATCH_INTERVAL_MS = 16;

export function Fader(props: FaderProps) {
  const [dragging, setDragging] = createSignal(false);
  // BUGFIX 2026-07-28: the in-drag value lives HERE, not in the store.
  // Writing to the store on every move replaces the item object, which
  // makes App.tsx's `<For each={layoutItems()}>` recreate this row -
  // destroying the component and its pointer capture, so the drag died
  // after ONE event. Measured: the row is removed+re-added once mid-drag,
  // and a 90%->10% drag landed on 13 (its pointerdown value) instead of
  // ~114. Shipped broken since the item landed; the old smoke only
  // asserted ">=1 POST fired", which pointerdown alone satisfies.
  // Committing once on release is also simply correct - a 60Hz drag has
  // no business touching persistence, undo history and the bridge-sync
  // debounce on every frame.
  const [liveValue, setLiveValue] = createSignal<number | null>(null);
  const shownValue = () => liveValue() ?? props.item.value;
  let lastDispatchMs = 0;
  let pendingValue: number | null = null;
  let padEl: HTMLDivElement | undefined;

  // Show-mode guard (SD-5): a quick brush sends nothing; a long-press
  // "grabs" the fader (handle lights live) and then it tracks the drag.
  // Releasing re-locks — every drag session needs a fresh grab.
  const [grabbed, setGrabbed] = createSignal(false);
  let grabTimer: number | undefined;
  let grabStartX = 0;
  let grabStartY = 0;
  function beginPendingGrab(x: number, y: number): void {
    grabStartX = x;
    grabStartY = y;
    if (grabTimer !== undefined) clearTimeout(grabTimer);
    grabTimer = window.setTimeout(() => {
      grabTimer = undefined;
      setGrabbed(true);
      setDragging(true);
      commit(valueFromPointer(grabStartX, grabStartY));
    }, GUARD_GRAB_HOLD_MS);
  }
  function cancelPendingGrab(): void {
    if (grabTimer !== undefined) { clearTimeout(grabTimer); grabTimer = undefined; }
  }
  onCleanup(() => { if (grabTimer !== undefined) clearTimeout(grabTimer); });
  // Entering edit mode drops any grab / pending grab (edit ignores guards, SD-5).
  createEffect(() => { if (isEditMode()) { cancelPendingGrab(); setGrabbed(false); } });

  function tokenFor(): string {
    return COLOR_TOKEN[props.item.color];
  }

  function isVertical(): boolean {
    return props.item.vertical !== false;
  }

  function valueFromPointer(clientX: number, clientY: number): number {
    if (!padEl) return shownValue();
    const rect = padEl.getBoundingClientRect();
    // Fraction along the long axis. Vertical: bottom = 0, top = 1. Horizontal:
    // left = 0, right = 1.
    let frac: number;
    if (isVertical()) {
      frac = 1 - (clientY - rect.top) / rect.height;
    } else {
      frac = (clientX - rect.left) / rect.width;
    }
    frac = Math.max(0, Math.min(1, frac));
    return Math.round(frac * 127);
  }

  function dispatchValue(value: number): void {
    // Coalesce: skip if we fired within the throttle window.
    const now = performance.now();
    if (now - lastDispatchMs < DISPATCH_INTERVAL_MS) {
      pendingValue = value;
      return;
    }
    lastDispatchMs = now;
    pendingValue = null;
    // Fan out to every configured bridge (multi-select, 2026-08-13) —
    // undefined bridgeIds means the default bridge, matching every other
    // item kind's contract.
    for (const target of fanoutTargets(props.item.bridgeIds)) {
      const step: MidiCcStep = {
        type: 'midi.cc',
        id: ulid(),
        target,
        channel: props.item.channel + 1,
        cc: props.item.ccNumber,
        value,
      };
      void fireAction(step);
    }
  }

  function commit(value: number): void {
    setLiveValue(value);
    dispatchValue(value);
    noteControlValue(props.item.id, undefined, value);
  }

  /** End of gesture: persist what the drag landed on, hand display back
   *  to the store. */
  function persist(): void {
    const v = liveValue();
    if (v !== null) editItem(props.item.id, { value: v } as Partial<FaderItem>);
    // The hand let go: a timeline holding this CC back takes it again per its touchMode (OPBridge S324).
    if (v !== null) releaseTouch(props.item.bridgeIds, [{ channel: props.item.channel, cc: props.item.ccNumber }]);
    setLiveValue(null);
  }

  function onPointerDown(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    e.preventDefault();
    const guarded = !isEditMode() && !!props.item.guard;
    if (guarded && !grabbed()) {
      // Long-press to grab; a quick touch is ignored. Capture so we keep
      // getting move/up while the grab timer runs.
      e.currentTarget.setPointerCapture(e.pointerId);
      beginPendingGrab(e.clientX, e.clientY);
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    commit(valueFromPointer(e.clientX, e.clientY));
  }

  function onPointerMove(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (grabTimer !== undefined) {
      // Pre-grab: a swipe past the tolerance cancels the pending grab (it
      // was a brush, not a deliberate press-to-grab). Nothing sent.
      if (Math.abs(e.clientX - grabStartX) > GUARD_GRAB_MOVE_TOL ||
          Math.abs(e.clientY - grabStartY) > GUARD_GRAB_MOVE_TOL) {
        cancelPendingGrab();
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
      }
      return;
    }
    if (!dragging()) return;
    e.stopPropagation();
    commit(valueFromPointer(e.clientX, e.clientY));
  }

  function onPointerUp(e: PointerEvent & { currentTarget: HTMLDivElement }) {
    if (grabTimer !== undefined) {
      // Released before the grab landed — quick touch, ignore entirely.
      cancelPendingGrab();
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* unsupported */ }
      return;
    }
    if (!dragging()) return;
    e.stopPropagation();
    e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
    setGrabbed(false); // re-lock — a guarded fader needs a fresh grab next time
    // Flush any pending value coalesced by the throttle so the final position
    // always gets on the wire even if the last move fell inside the window.
    if (pendingValue !== null) {
      lastDispatchMs = 0;
      commit(pendingValue);
    }
    persist();
  }

  // Handle position as a % along the fader's long axis.
  function handleOffsetPct(): number {
    return (shownValue() / 127) * 100;
  }

  /* FACE FLAGS (2026-08-09). Operator: *"noticed on the faders at least, that
     show name and show info both don't make a difference on or off."* They
     didn't: the Inspector offers `hideName` / `hideLabel` on EVERY kind, and
     the fader read neither - it had no name element at all, and its readout
     was unconditional. Two toggles that moved nothing, on the control the
     operator happened to try them on.

     `name` is the authored override only, with no auto-derived fallback: a
     fader whose face said "FADER" would be worse than one that says nothing,
     and CC/channel is already the readout's job. So an unnamed fader renders
     exactly as before - this adds a row only where the author asked for one,
     which is also why it cannot move `skin_matches_workbench`. */
  const name = () => props.item.name?.trim() ?? '';
  const showName = () => !props.item.hideName;
  const showInfo = () => !props.item.hideLabel;

  return (
    <div
      class="fader"
      data-vertical={isVertical() ? 'true' : 'false'}
      data-token={tokenFor()}
      data-dragging={dragging() ? 'true' : 'false'}
      data-grabbed={grabbed() ? 'true' : 'false'}
      ref={padEl}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div class="fader-track" />
      <div
        class="fader-fill"
        style={
          isVertical()
            ? { height: `${handleOffsetPct()}%` }
            : { width: `${handleOffsetPct()}%` }
        }
      />
      <div
        class="fader-handle"
        style={
          isVertical()
            ? { bottom: `calc(${handleOffsetPct()}% - 8px)` }
            : { left: `calc(${handleOffsetPct()}% - 8px)` }
        }
      />
      {showName() && name() && (
        <div class="fader-name">{name()}</div>
      )}
      {showInfo() && (
        <div class="fader-readout">
          <span class="fader-cc">cc {props.item.ccNumber} · ch {props.item.channel + 1}</span>
          <span class="fader-value">{shownValue()}</span>
        </div>
      )}
    </div>
  );
}
